import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { APPLICATION_ID, HANDOFF_EXCHANGE_URL } from '../config';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import { persistAuthSession, resolvePostAuthPath } from '../utils/authSession';
import {
    clearOnboardingState,
    extractHandoffErrorMessage,
    parseHandoffParams,
    peekOnboardingState,
} from '../utils/coreOnboarding';
import './login.css';

let handoffExchangePromise = null;

function exchangeHandoffCode(code, state) {
    if (!handoffExchangePromise) {
        handoffExchangePromise = (async () => {
            const response = await fetch(HANDOFF_EXCHANGE_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    code,
                    applicationId: APPLICATION_ID,
                    state,
                }),
            });

            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(extractHandoffErrorMessage(data));
            }
            if (!data.accessToken) {
                throw new Error('Sign-up completed, but no session was returned.');
            }
            return data;
        })();
    }

    return handoffExchangePromise;
}

function OnboardingCallback() {
    const navigate = useNavigate();
    const { clearSessionExpired } = useAuth();
    const { fetchUserPermissions, clearPermissions } = usePermissions();
    const [error, setError] = useState('');

    useEffect(() => {
        const { code, state, error: handoffError, errorDescription } = parseHandoffParams();

        const fail = (message) => {
            clearOnboardingState();
            setError(message);
        };

        if (handoffError) {
            fail(errorDescription || handoffError || 'Sign-up was cancelled.');
            return undefined;
        }

        if (!code || !state) {
            fail('This sign-up link is missing required details. Please create an account again.');
            return undefined;
        }

        const expectedState = peekOnboardingState();
        if (!expectedState || expectedState !== state) {
            fail('This sign-up session expired. Please create an account again.');
            return undefined;
        }

        let cancelled = false;

        const completeHandoff = async () => {
            try {
                const data = await exchangeHandoffCode(code, state);
                persistAuthSession(data);
                clearOnboardingState();
                clearPermissions();
                clearSessionExpired();

                const tenants = await fetchUserPermissions();
                if (!cancelled) {
                    navigate(resolvePostAuthPath(tenants), { replace: true });
                }
            } catch (err) {
                if (!cancelled) {
                    fail(err.message || 'Could not complete sign-up. Please try again.');
                }
            }
        };

        completeHandoff();

        return () => {
            cancelled = true;
        };
    }, [clearSessionExpired, clearPermissions, fetchUserPermissions, navigate]);

    return (
        <div className="login-container">
            <div className="login-content">
                <div className="login-card">
                    <div className="login-header">
                        <div className="logo-container">
                            <img
                                src="/favicon.ico"
                                alt="Wandawazi Flow Logo"
                                className="login-logo"
                                style={{ width: '120px', height: 'auto', maxHeight: '50px', objectFit: 'contain', filter: 'none' }}
                            />
                        </div>
                        <h1 className="login-title">Finishing sign-up</h1>
                        <p className="login-subtitle">
                            {error ? 'We could not complete your sign-up' : 'Securely connecting your new account'}
                        </p>
                    </div>

                    {error ? (
                        <>
                            <div className="error-message">{error}</div>
                            <div className="onboarding-status">
                                <Link to="/" className="forgot-password-link">Back to sign in</Link>
                            </div>
                        </>
                    ) : (
                        <div className="onboarding-status">
                            <LoadingSpinner size="md" variant="primary" message="Signing you in..." />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default OnboardingCallback;
